import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { catchError, finalize, of, switchMap } from 'rxjs';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { CompanyProductMonthlyEconomicReport } from 'src/app/models/company_product_monthly_economic_report.model';
import { CompanyProductMonthlyEconomicReportInvoiceConciliation } from 'src/app/models/company_product_monthly_economic_report_invoice_conciliation.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import {
  buildInvoiceLineLabel,
  resolveCompanyProductIdFromRouteAncestors,
} from '../company-product-monthly-economic-report-conciliation/company-product-monthly-economic-report-conciliation.component';

interface ConciliationRowSerialized {
  id: number;
  invoice_number: string;
  invoice_line_label: string;
  invoice_date_label: string;
  hours_conciliated_label: string;
  amount_conciliated_label: string;
  comment_label: string;
  is_automatic: boolean;
  user_label: string;
  can_delete: boolean;
}

@Component({
  selector: 'app-company-product-monthly-economic-report-detail',
  templateUrl: './company-product-monthly-economic-report-detail.component.html',
  styleUrl: './company-product-monthly-economic-report-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CurrencyPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatTooltipModule,
    RouterLink,
    TypeSafeMatCellDeferred,
  ],
})
export class CompanyProductMonthlyEconomicReportDetailComponent {
  private readonly api_service = inject(ApiService);
  private readonly employee = inject(EmployeeService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly form_builder = inject(FormBuilder);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly permissions_service = inject(PermissionsService);
  private readonly destroy_ref = inject(DestroyRef);

  private readonly route_params = toSignal(this.activated_route.paramMap, {
    initialValue: this.activated_route.snapshot.paramMap,
  });
  private readonly reload_trigger = signal(0);

  readonly is_loading = signal(true);
  readonly is_saving_comment = signal(false);
  readonly is_editing_comment = signal(false);

  readonly comment_control = this.form_builder.nonNullable.control<string>('');

  readonly fixed_company_product_id = computed((): number | null => resolveCompanyProductIdFromRouteAncestors(this.activated_route));

  readonly list_back_link = computed((): string[] => {
    const fixed_company_product_id = this.fixed_company_product_id();
    if (fixed_company_product_id !== null) {
      return [
        '/employees',
        'development',
        'company-products',
        String(fixed_company_product_id),
        'company-product-monthly-economic-reports',
      ];
    }

    return ['/employees', 'production', 'company-product-monthly-economic-reports'];
  });

  readonly back_query_params = computed(
    (): Record<string, unknown> => removeCompanyProductIdFromQueryParams(this.activated_route.snapshot.queryParams)
  );

  private readonly load_request = computed(() => {
    this.reload_trigger();
    const pmer_id = Number(this.route_params().get('pmer_id') ?? 0);
    return pmer_id;
  });

  private readonly report_result = toSignal(
    toObservable(this.load_request).pipe(
      switchMap(pmer_id => {
        if (!pmer_id) {
          this.is_loading.set(false);
          return of(null);
        }

        this.is_loading.set(true);
        return this.api_service
          .getCompanyProductMonthlyEconomicReport(pmer_id, {
            with: 'company_product,company_product_monthly_economic_report_invoice_conciliations.invoice,company_product_monthly_economic_report_invoice_conciliations.invoice_line,company_product_monthly_economic_report_invoice_conciliations.user',
            append: COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_DETAIL_APPEND,
          })
          .pipe(
            catchError(() => {
              this.snack_bar.open('No se pudo cargar el periodo.', '', { duration: 3000 });
              return of(null);
            }),
            finalize(() => this.is_loading.set(false))
          );
      })
    ),
    { initialValue: null as CompanyProductMonthlyEconomicReport | null }
  );

  readonly report = signal<CompanyProductMonthlyEconomicReport | null>(null);

  readonly can_edit_comment = computed(
    (): boolean => this.permissions_service.isDepartment('direction') || this.permissions_service.isDepartment('rrhh')
  );

  readonly month_label = computed((): string => {
    const current_report = this.report();
    if (!current_report) {
      return '';
    }
    const formatted = dayjs(current_report.billed_start_date).locale('es').format('MMMM YYYY');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  });

  readonly company_product_title = computed((): string => this.report()?.company_product?.title ?? '');

  readonly conciliations_data_source = computed(() => {
    const data_source = new MatTableDataSource<ConciliationRowSerialized>();
    data_source.data = this.serializeConciliations(this.report()?.company_product_monthly_economic_report_invoice_conciliations ?? []);
    return data_source;
  });

  readonly displayed_columns = ['invoice_number', 'invoice_line', 'invoice_date', 'hours', 'amount', 'comment', 'origin', 'actions'];

  constructor() {
    effect(() => {
      const result = this.report_result();
      this.report.set(result);
      this.comment_control.setValue(result?.comment ?? '', { emitEvent: false });
    });
  }

  startEditingComment(): void {
    if (!this.can_edit_comment()) {
      return;
    }
    this.is_editing_comment.set(true);
  }

  cancelEditingComment(): void {
    const current_report = this.report();
    this.comment_control.setValue(current_report?.comment ?? '');
    this.is_editing_comment.set(false);
  }

  saveComment(): void {
    const current_report = this.report();
    if (!current_report || this.is_saving_comment()) {
      return;
    }

    const new_comment = this.comment_control.value.trim() === '' ? null : this.comment_control.value;
    this.is_saving_comment.set(true);
    this.api_service
      .updateCompanyProductMonthlyEconomicReport(current_report.id, { comment: new_comment })
      .pipe(
        catchError(() => {
          this.snack_bar.open('No se pudo guardar el comentario.', '', { duration: 3000 });
          return of(null);
        }),
        finalize(() => this.is_saving_comment.set(false)),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(result => {
        if (result === null) {
          return;
        }
        this.snack_bar.open('Comentario guardado.', '', { duration: 2000 });
        this.is_editing_comment.set(false);
        this.report.set({ ...current_report, comment: new_comment });
      });
  }

  deleteConciliation(conciliation_id: number): void {
    if (!confirm('¿Eliminar esta conciliación?')) {
      return;
    }

    this.api_service
      .destroyCompanyProductMonthlyEconomicReportInvoiceConciliation(conciliation_id)
      .pipe(
        catchError(() => {
          this.snack_bar.open('No se pudo eliminar la conciliación.', '', { duration: 3000 });
          return of(null);
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(() => {
        this.snack_bar.open('Conciliación eliminada.', '', { duration: 2000 });
        this.reload_trigger.update(value => value + 1);
        this.employee.refreshActionsRequired();
      });
  }

  navigateToConciliation(): void {
    const current_report = this.report();
    if (!current_report) {
      return;
    }

    const current_query_params = this.activated_route.snapshot.queryParams;
    const fixed_company_product_id = this.fixed_company_product_id();

    if (fixed_company_product_id !== null) {
      this.router.navigate(
        [
          '/employees',
          'development',
          'company-products',
          fixed_company_product_id,
          'company-product-monthly-economic-reports',
          'conciliation',
        ],
        { queryParams: current_query_params }
      );
      return;
    }

    this.router.navigate(['/employees', 'production', 'company-product-monthly-economic-reports', 'conciliation'], {
      queryParams: {
        ...current_query_params,
        company_product_id: current_report.company_product_id,
      },
    });
  }

  private serializeConciliations(conciliations: CompanyProductMonthlyEconomicReportInvoiceConciliation[]): ConciliationRowSerialized[] {
    return conciliations.map(conciliation => ({
      id: conciliation.id,
      invoice_number: conciliation.invoice?.invoice_number ?? '—',
      invoice_line_label: buildInvoiceLineLabel(conciliation.invoice_line) ?? '—',
      invoice_date_label: conciliation.invoice?.invoice_date ? dayjs(conciliation.invoice.invoice_date).format('DD/MM/YYYY') : '—',
      hours_conciliated_label: `${Number(conciliation.hours_conciliated).toFixed(2)}h`,
      amount_conciliated_label: `${Number(conciliation.amount_conciliated).toFixed(2)} €`,
      comment_label: (conciliation.comment ?? '').trim() !== '' ? (conciliation.comment ?? '').trim() : '—',
      is_automatic: conciliation.is_automatic,
      user_label: conciliation.is_automatic
        ? 'Automática'
        : `${conciliation.user?.name ?? ''} ${conciliation.user?.surnames ?? ''}`.trim() || 'Manual',
      can_delete: true,
    }));
  }
}

const COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_DETAIL_APPEND = 'ro_total_hours_billed,ro_total_amount_billed';

function removeCompanyProductIdFromQueryParams(query_params: Record<string, unknown>): Record<string, unknown> {
  const { company_product_id: _company_product_id, ...params_without_company_product_id } = query_params;
  return params_without_company_product_id;
}
